<template>
	<div
		class="v2-toggle-button-group"
		:class="[fullWidth ? '-full-width' : '', theme ? `-t-${theme}` : '', rounded ? '-rounded' : '', onWhiteBackground ? '-on-white-background' : '']"
	>
		<div
			v-for="button in buttons"
			:key="button.name + button.id"
			class="v2-toggle-button-group__wrapper"
			:class="{'is-disabled': button.disabled}"
		>
			<input
				:id="buttonId(button.id)"
				class="v2-toggle-button-group__input"
				type="radio"
				:name="name"
				:value="button.id"
				:checked="selectedButtonId == button.id"
				:disabled="disabled || button.disabled"
				:readonly="dataReadonly"
				:required="required"
			>
			<label
				:for="buttonId(button.id)"
				class="v2-toggle-button-group__label"
				:class="{'-ellipsis': ellipsis}"
				@click="onButtonClick($event, button.id, button.disabled)"
			>{{ translationKey ? $I18n.trans(translationKey + button.name) : button.name
			}}</label>
		</div>
	</div>
</template>

<script>
const themes = [
	'dark',
];

export default {
	name:  'ToggleButtonGroup',
	props: {
		name: {
			type:     String,
			required: true,
		},
		uniqueId: {
			type:     String,
			required: true,
		},
		buttons: {
			type:    Array,
			default: () => [],
		},
		selectedId: {
			type:    [String, Number, Boolean],
			default: null,
		},
		bubble:         { type: Boolean, default: false },
		fullWidth:      { type: Boolean, default: false },
		canEmitChange:  { type: Boolean, default: true },
		emitAutoSelect: { type: Boolean, default: false },
		disabled:       { type: Boolean, default: false },
		readonly:       { type: Boolean, default: false },
		translationKey: {
			type:    String,
			default: null,
		},
		theme: {
			type:    String,
			default: null,
			validator(theme) {
				return themes.includes(theme);
			},
		},
		required: {
			type:    Boolean,
			default: false,
		},
		ellipsis: {
			type:    Boolean,
			default: true,
		},
		rounded: {
			type:    Boolean,
			default: false,
		},
		onWhiteBackground: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			selectedButtonId: this.selectedId,
			dataReadonly:     this.readonly,
		};
	},
	computed: {
		buttonCount() {
			return this.buttons.length;
		},
	},
	watch: {
		buttons() {
			this.dataReadonly = this.buttonCount === 1;
			if (this.buttonCount === 1) {
				this.selectedButtonId = this.buttons[0].id;
				if (this.canEmitChange && this.emitAutoSelect) {
					this.emitChange(this.selectedButtonId, true);
				}
			}
		},
		selectedButtonId() {
			if (this.selectedId && !this.selectedButtonId) {
				this.selectedButtonId = this.selectedId;
			}
		},
		selectedId(id) {
			this.selectedButtonId = id;
			if (this.canEmitChange) {
				this.emitChange(id, true);
			}
		},
	},
	mounted() {
		this.dataReadonly = this.buttons && Object.keys(this.buttons).length === 1;
	},
	methods: {
		onButtonClick(event, id, disabled) {
			if (!this.dataReadonly && !this.disabled && !disabled) {
				if (!this.bubble) {
					this.selectedButtonId = id;
				}
				this.emitChange(id);
			} else {
				event.preventDefault();
			}
		},
		buttonId(index) {
			return this.name + '_' + '_' + this.uniqueId + index;
		},
		unselect() {
			if (!this.disabled || !this.dataReadonly) {
				this.selectedButtonId = null;
			}
		},
		emitChange(id, autoChanged) {
			if (id === null) {
				return;
			}
			// autoChanged is false if change came from direct item selection
			this.$emit('change', {
				id:   id,
				// eslint-disable-next-line no-self-compare
				name: this.buttons.find(button => button.id === id).name,
			}, autoChanged);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.v2-toggle-button-group {
	--bg-color: var(--color-grey-050);
	--border-color: var(--color-grey-200);
	--text-color: var(--color-grey-400);
	--text-color-checked: var(--color-green-petrol);
	--text-color-hover: var(--color-green-petrol);
	--border-color-hover: var(--color-black);
	--bg-color-hover: var(--color-grey-050);
	--bg-color-checked: var(--color-white);

	display: flex;
	max-width: 100%;
	padding-right: 1px;
	background-color: var(--bg-color);
	border-radius: var(--t-form-border-radius);
	border: 1px solid var(--border-color);
	height: $form-input-height;
	overflow: hidden;

	&.-on-white-background {
		--bg-color: var(--color-white);
		--bg-color-hover: var(--color-white);
		--bg-color-checked: var(--color-grey-050);
	}

	&.-t-dark {
		--bg-color: transparent;
		--border-color: #{$form-color-border--dark};
		--text-color: #{$form-color-input-placeholder--dark};
		--text-color-hover: #{$form-color-input-text--dark};
		--text-color-checked: #{$form-color-input-text--dark};
		--border-color-hover: #{$form-color-border--dark};
		--bg-color-hover: transparent;
		--bg-color-checked: var(--color-grey-800);
	}

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type, selector-max-type, max-nesting-depth
		--bg-color: transparent;
		--border-color: var(--color-grey-800);
		--text-color: var(--color-grey-500);
		--text-color-hover: var(--color-grey-100);
		--text-color-checked: var(--color-grey-100);
		--border-color-hover: var(--color-grey-800);
		--bg-color-hover: transparent;
		--bg-color-checked: var(--color-grey-950);
	}

	&.-rounded {
		border-radius: var(--t-form-border-radius-rounded);
	}
}

.v2-toggle-button-group__wrapper {
	line-height: 0;
	overflow: hidden;

	.v2-toggle-button-group:not(.-full-width) & {
		min-width: 85px;
	}

	.v2-toggle-button-group.-full-width & {
		flex-grow: 1;
	}

	@include size(sm-up) {
		flex-basis: 140px;
	}
}

.v2-toggle-button-group__input {
	@include visually-hidden;
}

.v2-toggle-button-group__label {
	display: inline-block;
	position: relative;
	width: 100%;
	margin-bottom: 0;
	padding: 15px 12px;
	text-align: center;
	font-size: $button-font-size--sm; // stylelint-disable-line
	line-height: 100%;
	color: var(--text-color);
	cursor: pointer;

	&.-ellipsis {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	// .v2-toggle-button-group__wrapper:first-child & {
	// 	border-top-left-radius: var(--t-form-border-radius);
	// 	border-bottom-left-radius: var(--t-form-border-radius);

	// 	.v2-toggle-button-group.-rounded & {
	// 		border-top-left-radius: var(--t-form-border-radius-rounded);
	// 		border-bottom-left-radius: var(--t-form-border-radius-rounded);
	// 	}
	// }

	// .v2-toggle-button-group__wrapper:last-child & {
	// 	border-top-right-radius: var(--t-form-border-radius);
	// 	border-bottom-right-radius: var(--t-form-border-radius);

	// 	.v2-toggle-button-group.-rounded & {
	// 		border-top-right-radius: var(--t-form-border-radius-rounded);
	// 		border-bottom-right-radius: var(--t-form-border-radius-rounded);
	// 	}
	// }

	.v2-toggle-button-group__input:not(:disabled):not(:checked) + & {
		&:focus,
		&:hover {
			color: var(--text-color-hover);
			background-color: var(--bg-color-hover);
			z-index: 1;
		}
	}

	.v2-toggle-button-group__input:checked + & {
		color: var(--text-color-checked);
		background-color: var(--bg-color-checked);
		z-index: 2;
	}

	// Disabled state
	.v2-toggle-button-group__input:disabled + & {
		cursor: not-allowed;
	}

	@include size(md-up) {
		font-size: $button-font-size; // stylelint-disable-line
	}
}
</style>
